import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "../raadmin-template/Services/toast";
import DynamicIcon from "../components/DynamicIcon";
import { getMaintenancesList, getMaintenanceDetail, resetSelectedMaintenance, selectMaintenancesList, selectMaintenancesLoading, selectMaintenancesTotalRecords } from "../redux/maintenances/maintenancesSlice";
import { selectAccountData } from "../redux/main/accountSlice";
import { closeModal, resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import LoadingSection from "../components/LoadingSection";
import DynamicTitle from "../components/DynamicTitle";
import RightModal from "../components/templates/RightModal";
import AddEditMaintenance from "../components/maintenancesPage/AddEditMaintenance";
import ViewMaintenance from "../components/maintenancesPage/ViewMaintenance";
import { getNodeApiUrl } from "../config/api.config";
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from "../components/DataTable/DataTable";

export default function MaintenancesList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const palette = useColorPalette();
    const maintenancesList = useSelector(selectMaintenancesList);
    const maintenancesTotalRecords = useSelector(selectMaintenancesTotalRecords);
    const maintenancesLoading = useSelector(selectMaintenancesLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [expirationFilter, setExpirationFilter] = useState('all'); // 'all', 'valid', 'expiring', 'expired', 'nodate'
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    
    // Stato per la modale di conferma duplicazione
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [maintenanceToDuplicate, setMaintenanceToDuplicate] = useState(null);
    const [isDuplicating, setIsDuplicating] = useState(false);
    
    // Stati per la modale di eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [maintenanceToDelete, setMaintenanceToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Debug output delle manutenzioni ricevute
    useEffect(() => {
        console.log('Maintenances in component:', maintenancesList);
    }, [maintenancesList]);
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalMaintenanceId = useSelector(selectRightModalId);
    
    // Funzione per aggiornare la pagina con paginazione
    // pageIndex è l'indice di pagina (0-based) e non più l'offset
    const handlePageChange = (pageIndex, pageSize) => {
        // Calcoliamo l'offset start in base all'indice di pagina
        const start = pageIndex * pageSize;
        
        dispatch(getMaintenancesList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            expirationFilter: expirationFilter,
            start,
            length: pageSize
        }));
        dispatch(resetRefreshNow());
    };
    
    // Funzione per gestire la ricerca
    const handleSearch = (value) => {
        setSearchTerm(value);
        dispatch(getMaintenancesList({
            accountId: accountData?._id,
            token,
            search: value.length >= 3 ? value : '',
            expirationFilter: expirationFilter,
            start: 0,
            length: 25
        }));
        dispatch(resetRefreshNow());
    };
    
    // Funzione per gestire il filtro stato scadenza contratto
    const handleExpirationFilterChange = (value) => {
        setExpirationFilter(value);
        dispatch(getMaintenancesList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            expirationFilter: value,
            start: 0,
            length: 25
        }));
        dispatch(resetRefreshNow());
    };
    
    // Carica le manutenzioni quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getMaintenancesList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                expirationFilter: expirationFilter,
                start: 0,
                length: 25
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, token]);
    
    // Funzioni per le azioni sui bottoni
    const handleViewClick = (e) => {
        e.stopPropagation();
        const maintenanceId = e.currentTarget.closest('[data-id]').getAttribute('data-id');
        dispatch(setModal({
            isOpen: true,
            mode: 'view',
            id: maintenanceId
        }));
    };

    const handleEditClick = (e, maintenance) => {
        e.stopPropagation();
        
        // Verifica se i dati necessari esistono ancora
        const missingData = [];
        if (!maintenance.customerId || !maintenance.customerLabel) {
            missingData.push('cliente');
        }
        if (!maintenance.systemId || !maintenance.systemLabel) {
            missingData.push('impianto');
        }
        if (!maintenance.systemAddressId && !maintenance.systemAddressLabel) {
            missingData.push('indirizzo');
        }
        
        if (missingData.length > 0) {
            toast.error(`Impossibile modificare questa manutenzione: ${missingData.join(', ')} non più disponibile/i. I dati potrebbero essere stati eliminati.`);
            return;
        }
        
        // Reindirizza alla pagina di modifica manutenzione
        navigate(`/maintenance/edit/${maintenance._id}`);
    };
    
    // Gestione click duplica
    const handleDuplicateClick = (e, maintenance) => {
        e.stopPropagation();
        
        // Verifica se i dati necessari esistono ancora
        const missingData = [];
        if (!maintenance.customerId || !maintenance.customerLabel) {
            missingData.push('cliente');
        }
        if (!maintenance.systemId || !maintenance.systemLabel) {
            missingData.push('impianto');
        }
        if (!maintenance.systemAddressId && !maintenance.systemAddressLabel) {
            missingData.push('indirizzo');
        }
        
        if (missingData.length > 0) {
            toast.error(`Impossibile duplicare questa manutenzione: ${missingData.join(', ')} non più disponibile/i. I dati potrebbero essere stati eliminati.`);
            return;
        }
        
        setMaintenanceToDuplicate(maintenance);
        setShowDuplicateModal(true);
    };
    
    // Conferma duplicazione
    const confirmDuplicate = async () => {
        if (!maintenanceToDuplicate) return;
        
        setIsDuplicating(true);
        try {
            // Prepara i dati per la duplicazione (rimuovi _id e code)
            const duplicateData = {
                accountId: maintenanceToDuplicate.accountId || accountData._id,
                customerId: maintenanceToDuplicate.customerId,
                customerLabel: maintenanceToDuplicate.customerLabel,
                systemId: maintenanceToDuplicate.systemId,
                systemLabel: maintenanceToDuplicate.systemLabel,
                systemAddressId: maintenanceToDuplicate.systemAddressId,
                systemAddressLabel: maintenanceToDuplicate.systemAddressLabel,
                systemTypes: maintenanceToDuplicate.systemTypes || [],
                notes: maintenanceToDuplicate.notes || '',
                actions: (maintenanceToDuplicate.actions || []).map(action => ({
                    title: action.title,
                    notes: action.notes || '',
                    month: action.month,
                    year: action.year,
                    statusId: action.statusId,
                    statusLabel: action.statusLabel
                })),
                geoData: maintenanceToDuplicate.geoData,
                contractStartDate: maintenanceToDuplicate.contractStartDate,
                contractEndDate: maintenanceToDuplicate.contractEndDate,
                contractFile: maintenanceToDuplicate.contractFile
            };
            
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/maintenances`,
                { maintenance: duplicateData },
                { headers: { 'auth-token': token } }
            );
            
            if (response.data) {
                toast.success(`Manutenzione duplicata con successo! Nuovo codice: ${response.data.ret?.code || response.data.code || 'generato'}`);
                // Aggiorna la lista
                setDataTableKey(Date.now());
                dispatch(getMaintenancesList({
                    accountId: accountData._id,
                    token,
                    search: searchTerm.length >= 3 ? searchTerm : '',
                    start: 0,
                    length: 25
                }));
            }
        } catch (error) {
            console.error('Errore nella duplicazione:', error);
            toast.error('Errore durante la duplicazione della manutenzione');
        } finally {
            setIsDuplicating(false);
            setShowDuplicateModal(false);
            setMaintenanceToDuplicate(null);
        }
    };
    
    // Annulla duplicazione
    const cancelDuplicate = () => {
        setShowDuplicateModal(false);
        setMaintenanceToDuplicate(null);
    };
    
    // Gestione click elimina
    const handleDeleteClick = (e, maintenance) => {
        e.stopPropagation();
        setMaintenanceToDelete(maintenance);
        setShowDeleteModal(true);
    };
    
    // Conferma eliminazione
    const confirmDelete = async () => {
        if (!maintenanceToDelete) return;
        
        setIsDeleting(true);
        try {
            const response = await axios.delete(
                `${import.meta.env.VITE_API_BASE_URL}/maintenances/${maintenanceToDelete._id}`,
                {
                    headers: { 'auth-token': token }
                }
            );
            
            if (response.status === 200) {
                toast.success('Manutenzione eliminata con successo');
                // Aggiorna la lista
                dispatch(getMaintenancesList({
                    accountId: accountData._id,
                    token,
                    search: searchTerm.length >= 3 ? searchTerm : '',
                    expirationFilter,
                    start: 0,
                    length: 25
                }));
            }
        } catch (error) {
            console.error('Errore eliminazione:', error);
            toast.error('Errore durante l\'eliminazione della manutenzione');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setMaintenanceToDelete(null);
        }
    };
    
    // Annulla eliminazione
    const cancelDelete = () => {
        setShowDeleteModal(false);
        setMaintenanceToDelete(null);
    };
    
    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            // Aggiorna la tabella
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow && accountData?._id) {
            dispatch(getMaintenancesList({
                accountId: accountData._id,
                token
            }));
        }
    }, [rightModalIsRefreshNow, dispatch, accountData]);
    
    // Carica i dettagli della manutenzione quando il modale viene aperto per modifica o visualizzazione
    useEffect(() => {
        if (rightModalIsOpen && rightModalMaintenanceId && (rightModalMode === 'edit' || rightModalMode === 'view')) {
            dispatch(getMaintenanceDetail({
                maintenanceId: rightModalMaintenanceId,
                token
            }));
        }
    }, [rightModalIsOpen, rightModalMaintenanceId, rightModalMode, dispatch, token]);
    
    // Reset della manutenzione selezionata quando si chiude il modale
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetSelectedMaintenance());
        }
    }, [rightModalIsOpen, dispatch]);
    
    // Funzione per navigare alla pagina di creazione di una nuova manutenzione con wizard
    const handleAddMaintenance = () => {
        navigate('/maintenance/create');
    };
    
    return (
        <div className="page">
            {maintenancesLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Manutenzioni" />
            
            <h1><span className="font-light">Manutenzioni / </span>Lista Manutenzioni</h1>
            
            <div className="box" style={{ backgroundColor: palette?.boxBg || '#ffffff', color: palette?.boxText || '#000000', borderColor: palette?.boxBorder || '#e5e7eb' }}>
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="flex items-center gap-2 flex-1">
                        {/* Campo ricerca */}
                        <div className="relative min-w-[200px] max-w-md flex-1">
                            <input 
                                type="text" 
                                className="text-sm rounded-lg block w-full p-2.5 pr-10"
                                style={{
                                    backgroundColor: palette?.formInputBg || '#f9fafb',
                                    borderColor: palette?.formBorderColor || '#d1d5db',
                                    color: palette?.formInputText || '#111827',
                                    outlineColor: palette?.primary,
                                    border: '1px solid'
                                }}
                                placeholder="Cerca manutenzione..." 
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            {searchTerm ? (
                                <button 
                                    className="absolute inset-y-0 end-0 flex items-center pe-3"
                                    onClick={() => handleSearch('')}
                                >
                                    <DynamicIcon iconClass="fa-solid fa-times" style={{ color: palette?.iconColor || palette?.textColor }} />
                                </button>
                            ) : (
                                <div className="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none">
                                    <DynamicIcon iconClass="fa-solid fa-search" style={{ color: palette?.iconColor || palette?.textColor }} />
                                </div>
                            )}
                        </div>
                        
                        {/* Filtro garanzia */}
                        <select
                            className="text-sm rounded-lg p-2.5"
                            style={{
                                backgroundColor: palette?.formInputBg || '#f9fafb',
                                borderColor: palette?.formBorderColor || '#d1d5db',
                                color: palette?.formInputText || '#111827',
                                outlineColor: palette?.primary,
                                border: '1px solid',
                                minWidth: '160px'
                            }}
                            value={expirationFilter}
                            onChange={(e) => handleExpirationFilterChange(e.target.value)}
                        >
                            <option value="all">Tutti gli stati</option>
                            <option value="valid">🟢 In regola (oltre 1 mese)</option>
                            <option value="expiring">🟠 In scadenza (entro 1 mese)</option>
                            <option value="expired">🔴 Scaduti</option>
                            <option value="nodate">⚪ Senza data</option>
                        </select>
                    </div>
                    <button className="primary" onClick={handleAddMaintenance}>
                        <DynamicIcon iconClass="fa-solid fa-plus" />
                        Nuova Manutenzione
                    </button>
                </div>
                
                {/* Legenda scadenza contratti */}
                <div className="flex items-center gap-4 mb-3 text-sm">
                    <span className="font-medium">Legenda SCAD:</span>
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
                        <span>Oltre 1 mese</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-orange-500"></span>
                        <span>Entro 1 mese</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
                        <span>Scaduto</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-gray-400"></span>
                        <span>Nessuna data</span>
                    </div>
                </div>
                
                {/* Tabella manutenzioni */}
                <div className="w-full my-4">
                    <DataTable
                        key={dataTableKey}
                        columns={[
                            {
                                id: 'expiration',
                                title: 'SCAD',
                                width: '5%',
                                render: (maintenance) => {
                                    const today = new Date();
                                    const endDate = maintenance.contractEndDate ? new Date(maintenance.contractEndDate) : null;
                                    
                                    let colorClass = 'bg-gray-400'; // Default se non c'è data
                                    let tooltip = 'Nessuna data di scadenza';
                                    
                                    if (endDate) {
                                        const oneMonthFromNow = new Date();
                                        oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
                                        
                                        if (endDate <= today) {
                                            colorClass = 'bg-red-500';
                                            tooltip = `Scaduto il ${endDate.toLocaleDateString('it-IT')}`;
                                        } else if (endDate <= oneMonthFromNow) {
                                            colorClass = 'bg-orange-500';
                                            tooltip = `Scade il ${endDate.toLocaleDateString('it-IT')} (entro un mese)`;
                                        } else {
                                            colorClass = 'bg-green-500';
                                            tooltip = `Scade il ${endDate.toLocaleDateString('it-IT')}`;
                                        }
                                    }
                                    
                                    return (
                                        <div className="flex justify-center items-center">
                                            <span 
                                                className={`inline-block w-3 h-3 rounded-full ${colorClass}`}
                                                title={tooltip}
                                            />
                                        </div>
                                    );
                                }
                            },
                            {
                                id: 'code',
                                title: 'CODICE',
                                width: '10%',
                                render: (maintenance) => (
                                    <div className="text-center uppercase">
                                        <div className="font-normal">
                                            {maintenance.code || 'N/D'}
                                        </div>
                                    </div>
                                )
                            },
                            {
                                id: 'customer',
                                title: 'CLIENTE',
                                width: '16%',
                                render: (maintenance) => (
                                    <div className="text-center uppercase">
                                        <div className="font-normal flex items-center justify-center gap-1 flex-wrap">
                                            <span>{maintenance.customerLabel || 'CLIENTE NON SPECIFICATO'}</span>
                                            {maintenance.customerIsProspect && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 normal-case">
                                                    Prospect
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )
                            },
                            {
                                id: 'system',
                                title: 'IMPIANTO',
                                width: '15%',
                                render: (maintenance) => (
                                    <div className="text-center uppercase">
                                        <div className="font-normal">
                                            {maintenance.systemLabel || 'IMPIANTO NON SPECIFICATO'}
                                        </div>
                                    </div>
                                )
                            },
                            {
                                id: 'systemType',
                                title: 'TIPO IMPIANTO',
                                width: '15%',
                                render: (maintenance) => (
                                    <div className="text-center">
                                        {maintenance.systemTypes && maintenance.systemTypes.length > 0 ? (
                                            <div className="flex flex-wrap justify-center gap-1">
                                                {maintenance.systemTypes.map((type, index) => (
                                                    <span 
                                                        key={index} 
                                                        className="inline-block text-xs px-2 py-1 rounded bg-blue-100 text-blue-800"
                                                    >
                                                        {type.systemTypeLabel || type.label || 'N/D'}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-sm">-</span>
                                        )}
                                    </div>
                                )
                            },
                            {
                                id: 'address',
                                title: 'INDIRIZZO',
                                width: '15%',
                                render: (maintenance) => (
                                    <div className="text-center uppercase">
                                        <div className="font-normal">
                                            {maintenance.systemAddressLabel || 'INDIRIZZO NON SPECIFICATO'}
                                        </div>
                                    </div>
                                )
                            },
                            {
                                id: 'actions',
                                title: 'AZIONI',
                                width: '12%',
                                render: (maintenance) => (
                                    <div className="flex justify-center space-x-2" data-id={maintenance._id}>
                                        <button 
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleViewClick}
                                            title="Visualizza"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={(e) => handleEditClick(e, maintenance)}
                                            title="Modifica"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-gray-100 rounded-full"
                                            onClick={(e) => handleDuplicateClick(e, maintenance)}
                                            title="Duplica"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-copy" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-red-100 rounded-full"
                                            onClick={(e) => handleDeleteClick(e, maintenance)}
                                            title="Elimina"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash text-red-600" />
                                        </button>
                                    </div>
                                )
                            }
                        ]}
                        data={maintenancesList || []}
                        onPageChange={handlePageChange}
                        loading={maintenancesLoading}
                        totalRecords={maintenancesTotalRecords || 0}
                        pageSize={25}
                        emptyMessage="Nessuna manutenzione trovata"
                        isResponsive={true}
                        hasSearch={true}
                        onSearch={handleSearch}
                        initialSearchTerm={searchTerm}
                    />
                </div>
            </div>
            
            {/* RIGHT MODAL per inserimento, modifica o visualizzazione */}
            {
                rightModalIsOpen && (
                    <RightModal width="w-4/5">
                        {rightModalMode === 'add' && (
                            <AddEditMaintenance mode="add" />
                        )}
                        {rightModalMode === 'edit' && rightModalMaintenanceId && (
                            <AddEditMaintenance mode="edit" />
                        )}
                        {rightModalMode === 'view' && rightModalMaintenanceId && (
                            <ViewMaintenance />
                        )}
                    </RightModal>
                )
            }
            
            {/* Modale conferma duplicazione */}
            {showDuplicateModal && maintenanceToDuplicate && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 rounded-full p-2">
                                    <DynamicIcon iconClass="fa-solid fa-copy text-white text-xl" />
                                </div>
                                <h3 className="text-xl font-bold text-white">Duplica Manutenzione</h3>
                            </div>
                        </div>
                        
                        {/* Body */}
                        <div className="px-6 py-5">
                            <p className="text-gray-600 mb-4">
                                Stai per creare una copia della manutenzione selezionata. La nuova manutenzione avrà un codice univoco generato automaticamente.
                            </p>
                            
                            {/* Dettagli manutenzione */}
                            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                                <div className="flex items-center gap-2">
                                    <DynamicIcon iconClass="fa-solid fa-barcode text-gray-400" />
                                    <span className="text-sm text-gray-500">Codice:</span>
                                    <span className="font-semibold text-gray-800">{maintenanceToDuplicate.code}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <DynamicIcon iconClass="fa-solid fa-user text-gray-400" />
                                    <span className="text-sm text-gray-500">Cliente:</span>
                                    <span className="font-medium text-gray-700">{maintenanceToDuplicate.customerLabel}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <DynamicIcon iconClass="fa-solid fa-industry text-gray-400" />
                                    <span className="text-sm text-gray-500">Impianto:</span>
                                    <span className="font-medium text-gray-700">{maintenanceToDuplicate.systemLabel}</span>
                                </div>
                                {maintenanceToDuplicate.actions && maintenanceToDuplicate.actions.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <DynamicIcon iconClass="fa-solid fa-list-check text-gray-400" />
                                        <span className="text-sm text-gray-500">Azioni:</span>
                                        <span className="font-medium text-gray-700">{maintenanceToDuplicate.actions.length} programmate</span>
                                    </div>
                                )}
                            </div>
                            
                            <p className="text-sm text-gray-500 mt-4 flex items-center gap-2">
                                <DynamicIcon iconClass="fa-solid fa-info-circle text-blue-400" />
                                Tutte le informazioni verranno copiate nella nuova manutenzione.
                            </p>
                        </div>
                        
                        {/* Footer */}
                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={cancelDuplicate}
                                disabled={isDuplicating}
                                className="px-5 py-2.5 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmDuplicate}
                                disabled={isDuplicating}
                                className="px-5 py-2.5 rounded-lg font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-500/30 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isDuplicating ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Duplicazione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fa-solid fa-copy" />
                                        Conferma Duplicazione
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* MODALE CONFERMA ELIMINAZIONE */}
            {showDeleteModal && maintenanceToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
                            <h3 className="text-xl font-bold text-white flex items-center gap-3">
                                <DynamicIcon iconClass="fa-solid fa-trash" />
                                Conferma eliminazione manutenzione
                            </h3>
                        </div>
                        
                        {/* Content */}
                        <div className="p-6">
                            <p className="text-gray-600 mb-4">
                                Stai per eliminare la seguente manutenzione:
                            </p>
                            
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
                                <div className="flex items-center gap-2">
                                    <DynamicIcon iconClass="fa-solid fa-barcode text-gray-400" />
                                    <span className="text-sm text-gray-500">Codice:</span>
                                    <span className="font-semibold text-gray-800">{maintenanceToDelete.code}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <DynamicIcon iconClass="fa-solid fa-user text-gray-400" />
                                    <span className="text-sm text-gray-500">Cliente:</span>
                                    <span className="font-medium text-gray-700">{maintenanceToDelete.customerLabel || 'N/D'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <DynamicIcon iconClass="fa-solid fa-industry text-gray-400" />
                                    <span className="text-sm text-gray-500">Impianto:</span>
                                    <span className="font-medium text-gray-700">{maintenanceToDelete.systemLabel || 'N/D'}</span>
                                </div>
                            </div>
                            
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                <p className="text-yellow-800 text-sm">
                                    <strong>⚠️ Attenzione:</strong> Questa operazione è irreversibile. 
                                    Eliminando la manutenzione, verranno rimossi anche tutti i dati associati 
                                    (azioni programmate, contratto, note).
                                </p>
                            </div>
                            
                            <p className="text-gray-600">
                                Sei sicuro di voler procedere con l&apos;eliminazione?
                            </p>
                        </div>
                        
                        {/* Footer */}
                        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={cancelDelete}
                                disabled={isDeleting}
                                className="px-5 py-2.5 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={isDeleting}
                                className="px-5 py-2.5 rounded-lg font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all shadow-lg shadow-red-500/30 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isDeleting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Eliminazione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fa-solid fa-trash" />
                                        Conferma Eliminazione
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
